import { BookingLink } from "../components/BookingLink.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader.tsx";
import { SiteFooter } from "../components/SiteFooter.tsx";
import { api, ApiError, type PublicBooking } from "../lib/api.ts";
import {
  formatDuration,
  formatGbp,
  formatLongDate,
  formatTime,
} from "../lib/format.ts";
import { STUDIO } from "../lib/studio.ts";
import "./book.css";

/** How long to keep polling for the webhook to land after returning from Stripe. */
const CONFIRM_POLL_ATTEMPTS = 10;
const CONFIRM_POLL_INTERVAL_MS = 1500;

const STATUS_COPY: Record<string, { label: string; tone: string }> = {
  Confirmed: { label: "Confirmed", tone: "confirmed" },
  PendingPayment: { label: "Awaiting payment", tone: "pending" },
  Cancelled: { label: "Cancelled", tone: "cancelled" },
  Expired: { label: "Expired", tone: "cancelled" },
  Completed: { label: "Completed", tone: "confirmed" },
};

export function BookingDetail() {
  const { reference = "" } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") ?? "";
  const justPaid = params.get("paid") === "1";

  const [booking, setBooking] = useState<PublicBooking | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [checkingPolicy, setCheckingPolicy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null);
  const [waitingForPayment, setWaitingForPayment] = useState(justPaid);
  const pollsRemaining = useRef(CONFIRM_POLL_ATTEMPTS);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const result = await api.getBooking(reference, token, signal);
        setBooking(result);
        return result;
      } catch (caught) {
        if ((caught as Error)?.name === "AbortError") return null;
        setError(
          caught instanceof ApiError
            ? caught.message
            : "That booking could not be loaded.",
        );
        return null;
      }
    },
    [reference, token],
  );

  useEffect(() => {
    if (!token) {
      setError(
        "This link is incomplete. Please use the full link from your confirmation.",
      );
      return;
    }
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, token]);

  /**
   * Coming back from Stripe, the booking may still be PendingPayment for a
   * moment - the webhook is what confirms it and it may not have arrived yet.
   * Poll briefly so the client sees the confirmation without refreshing, rather
   * than a worrying "awaiting payment" on a card they have just paid.
   */
  useEffect(() => {
    if (!waitingForPayment || !booking) return;
    if (booking.status !== "PendingPayment") {
      setWaitingForPayment(false);
      return;
    }
    if (pollsRemaining.current <= 0) {
      setWaitingForPayment(false);
      return;
    }

    const timer = setTimeout(() => {
      pollsRemaining.current -= 1;
      void load();
    }, CONFIRM_POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [waitingForPayment, booking, load]);

  /**
   * Step one of cancelling: read the booking again, then ask.
   *
   * What happens to the deposit depends on how long is left before the
   * appointment, and this page may have been open since before the window
   * closed. Reading it again means the sentence the client is shown is the one
   * the API is about to act on, rather than whatever was true when they arrived.
   */
  async function requestCancel() {
    setError(null);
    setCheckingPolicy(true);
    const fresh = await load();
    setCheckingPolicy(false);
    if (!fresh) return;
    if (!fresh.cancellable) {
      setError(
        "This appointment can no longer be cancelled here. Please call the studio and we will sort it out.",
      );
      return;
    }
    setConfirming(true);
  }

  async function cancel() {
    if (!booking) return;

    setCancelling(true);
    setError(null);
    try {
      const updated = await api.cancelBooking(reference, token);
      setBooking(updated);
      setConfirmMessage(
        updated.message ?? "Your appointment has been cancelled.",
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The cancellation failed.",
      );
    } finally {
      setCancelling(false);
      // Either way the confirmation closes, so that whichever notice this left
      // behind - the cancellation, or the reason it failed - is on screen and
      // not behind the dialog.
      setConfirming(false);
    }
  }

  const status = booking
    ? (STATUS_COPY[booking.status] ?? {
        label: booking.status,
        tone: "pending",
      })
    : null;

  return (
    <>
      <SiteHeader variant="inner" />
      <main className="booking">
        <div className="page">
          {error && !booking && (
            <div className="confirmation">
              <h1 className="booking__title">We could not find that booking</h1>
              <p className="notice notice--error">{error}</p>
              <p className="booking__hint">
                If you have lost your link, call the studio on{" "}
                <a href={`tel:${STUDIO.phoneHref}`}>{STUDIO.phoneDisplay}</a>{" "}
                and we will find you.
              </p>
              <div className="confirmation__actions">
                <BookingLink className="btn btn--ghost">
                  Make a new booking
                </BookingLink>
              </div>
            </div>
          )}

          {!booking && !error && (
            <p className="booking__loading" aria-live="polite">
              <span className="spinner" /> Loading your booking…
            </p>
          )}

          {booking && status && (
            <div className="confirmation">
              <span
                className={`confirmation__status confirmation__status--${status.tone}`}
              >
                {status.label}
              </span>

              <h1 className="booking__title">
                {booking.status === "Confirmed"
                  ? "You are booked in"
                  : booking.status === "Cancelled"
                    ? "Appointment cancelled"
                    : booking.status === "Expired"
                      ? "This booking expired"
                      : "Almost there"}
              </h1>

              <p className="confirmation__reference">
                Reference <strong>{booking.reference}</strong>
              </p>

              {waitingForPayment && booking.status === "PendingPayment" && (
                <p className="notice" aria-live="polite">
                  <span className="spinner" /> Confirming your payment - this
                  takes a few seconds.
                </p>
              )}

              {!waitingForPayment && booking.status === "PendingPayment" && (
                <p className="notice">
                  We have not received a payment for this booking yet. If you
                  paid moments ago, refresh this page. Otherwise the slot will
                  be released shortly.
                </p>
              )}

              {confirmMessage && (
                <p className="notice notice--success">{confirmMessage}</p>
              )}
              {!confirmMessage && booking.refundSummary && (
                <p className="notice">{booking.refundSummary}</p>
              )}
              {error && <p className="notice notice--error">{error}</p>}

              <div
                className="panel stack-lg"
                style={{ marginTop: "var(--space-6)" }}
              >
                <dl className="detail-list">
                  <div>
                    <dt>Treatment</dt>
                    <dd>
                      {booking.serviceName}
                      <br />
                      <span className="field-hint">
                        {formatDuration(booking.durationMinutes)}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>When</dt>
                    <dd>
                      {booking.dateLabel}
                      <br />
                      <strong>{booking.timeLabel}</strong>
                    </dd>
                  </div>
                  <div>
                    <dt>Where</dt>
                    <dd>
                      {STUDIO.addressLines.join(", ")}
                      <br />
                      <span className="field-hint">
                        The full address is in your confirmation email.
                      </span>
                      <br />
                      <a
                        href={STUDIO.mapsUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        Open in Maps
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt>Booked for</dt>
                    <dd>
                      {booking.customerName}
                      <br />
                      <span className="field-hint">
                        {booking.customerEmail}
                      </span>
                    </dd>
                  </div>
                  {booking.discountLabel && booking.discountPence && (
                    <div>
                      <dt>{booking.discountLabel}</dt>
                      <dd>
                        &minus;{formatGbp(booking.discountPence)}
                        <br />
                        <span className="field-hint">
                          {formatGbp(
                            booking.pricePence + booking.discountPence,
                          )}{" "}
                          before the offer
                        </span>
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt>Deposit {booking.paid ? "paid" : "due"}</dt>
                    <dd>{formatGbp(booking.depositPence)}</dd>
                  </div>
                  <div>
                    <dt>
                      {booking.status === "Cancelled"
                        ? "Refunded"
                        : "Due on the day"}
                    </dt>
                    <dd>
                      {booking.status === "Cancelled"
                        ? booking.refundPence
                          ? formatGbp(booking.refundPence)
                          : "None"
                        : formatGbp(booking.balancePence)}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* The consultation form. Shown above the cancellation controls
                  because it is the thing they are here to do, and marked as
                  done rather than hidden once it is in - a client who filled it
                  in wants to see that it arrived. */}
              {booking.consultation &&
                booking.consultation.questionCount > 0 &&
                ["Confirmed", "PendingPayment"].includes(booking.status) && (
                  <div
                    className={`panel booking-consult ${
                      booking.consultation.submitted
                        ? "booking-consult--done"
                        : ""
                    }`}
                  >
                    <p className="eyebrow">Before your appointment</p>
                    {booking.consultation.submitted ? (
                      <>
                        <h2 className="booking-consult__title">
                          Your form is in
                        </h2>
                        <p className="booking-consult__lede">
                          Thank you - there is nothing else to do. You can still
                          change an answer if something has altered.
                        </p>
                        <Link
                          to={`/consultation/${booking.reference}?t=${encodeURIComponent(token)}`}
                          className="btn btn--ghost"
                        >
                          Review your answers
                        </Link>
                      </>
                    ) : (
                      <>
                        <h2 className="booking-consult__title">
                          Your consultation form
                        </h2>
                        <p className="booking-consult__lede">
                          {booking.consultation.questionCount} short question
                          {booking.consultation.questionCount === 1
                            ? ""
                            : "s"}{" "}
                          about your skin and your health, so your treatment is
                          chosen for you rather than guessed at. It takes about
                          three minutes.
                        </p>
                        <Link
                          to={`/consultation/${booking.reference}?t=${encodeURIComponent(token)}`}
                          className="btn"
                        >
                          Fill in your form
                        </Link>
                      </>
                    )}
                  </div>
                )}

              {booking.cancellable && (
                <div className="confirmation__actions">
                  {/* What cancelling would do to the deposit, before they
                      click - and again in the confirmation, because this is
                      the one thing nobody should learn from the email. */}
                  <p className="summary__policy">
                    {booking.cancellationOutlook}
                  </p>
                  {booking.refundIfCancelledNow > 0 && (
                    <p className="summary__policy">
                      To keep that refund, cancel by{" "}
                      <strong>
                        {formatLongDate(booking.freeCancellationUntilUtc)},{" "}
                        {formatTime(booking.freeCancellationUntilUtc)}
                      </strong>{" "}
                      - {booking.cancellationWindowHours} hours before your
                      appointment.
                    </p>
                  )}
                  <button
                    type="button"
                    className="btn btn--danger"
                    onClick={requestCancel}
                    disabled={checkingPolicy || cancelling}
                  >
                    {checkingPolicy ? (
                      <>
                        <span className="spinner" /> Checking your booking…
                      </>
                    ) : (
                      "Cancel this appointment"
                    )}
                  </button>
                  {confirming && (
                    <CancelConfirmation
                      booking={booking}
                      busy={cancelling}
                      onKeep={() => setConfirming(false)}
                      onConfirm={cancel}
                    />
                  )}
                </div>
              )}

              {booking.status === "Confirmed" && (
                <div className="confirmation__actions">
                  <p className="field-hint">
                    Keep this page bookmarked - it is the only way back to your
                    booking.
                  </p>
                  <p className="confirmation__manage-link">
                    {window.location.origin}/booking/{booking.reference}?t=
                    {token}
                  </p>
                </div>
              )}

              {(booking.status === "Cancelled" ||
                booking.status === "Expired") && (
                <div className="confirmation__actions">
                  <BookingLink className="btn">
                    Book another appointment
                  </BookingLink>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </>
  );
}

/**
 * The last word before an appointment is given up.
 *
 * A modal <dialog>, for the focus trap and the top layer that come with it -
 * and because a browser will quietly stop showing native prompts to anyone who
 * has seen a few of them, which is no way to deliver the one sentence about
 * somebody's money. The deposit's fate leads, the wording comes from the API so
 * the page cannot disagree with the policy, and the button that keeps the
 * appointment is the one holding focus.
 */
function CancelConfirmation({
  booking,
  busy,
  onKeep,
  onConfirm,
}: {
  booking: PublicBooking;
  busy: boolean;
  onKeep: () => void;
  onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    // showModal() looks for the autofocus attribute, which React does not
    // render, and otherwise takes the first focusable thing in the dialog -
    // here the studio's telephone number. Focus is placed by hand so it starts
    // on the button that keeps the appointment.
    keepRef.current?.focus();
    return () => dialog?.close();
  }, []);

  const refunding = booking.refundIfCancelledNow > 0;
  const forfeiting = !refunding && booking.paid && booking.depositPence > 0;

  return (
    <dialog
      ref={dialogRef}
      className="cancel-confirm"
      aria-labelledby="cancel-confirm-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onKeep();
      }}
    >
      <h2 className="cancel-confirm__title" id="cancel-confirm-title">
        Cancel this appointment?
      </h2>
      <p className="cancel-confirm__lede">
        {booking.serviceName} on {booking.dateLabel} at {booking.timeLabel}.
        This cannot be undone - the time goes back on the diary for someone
        else.
      </p>

      <p
        className={`cancel-confirm__deposit cancel-confirm__deposit--${
          refunding ? "refund" : "forfeit"
        }`}
      >
        {booking.cancellationOutlook}
      </p>

      {refunding && (
        <p className="field-hint">
          Refunds go back to the card you paid with and usually appear within a
          few working days, depending on your bank.
        </p>
      )}
      {forfeiting && (
        <p className="field-hint">
          The deposit was refundable until{" "}
          {formatLongDate(booking.freeCancellationUntilUtc)},{" "}
          {formatTime(booking.freeCancellationUntilUtc)}. If you would rather
          move the appointment than lose it, call the studio on{" "}
          <a href={`tel:${STUDIO.phoneHref}`}>{STUDIO.phoneDisplay}</a>.
        </p>
      )}

      <div className="cancel-confirm__actions">
        <button
          ref={keepRef}
          type="button"
          className="btn"
          onClick={onKeep}
          disabled={busy}
        >
          Keep my appointment
        </button>
        <button
          type="button"
          className="btn btn--danger"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? (
            <>
              <span className="spinner" /> Cancelling…
            </>
          ) : refunding ? (
            `Cancel and refund ${formatGbp(booking.refundIfCancelledNow)}`
          ) : forfeiting ? (
            "Cancel without a refund"
          ) : (
            "Cancel this appointment"
          )}
        </button>
      </div>
    </dialog>
  );
}
